import { Button, Colors, MenuItem } from '@blueprintjs/core';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { inject, observer } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import React from 'react';
import { TLCode } from '../../common/enums';
import { PharmacyDetailedStore, PharmacyStore } from '../../store';
import { Pharmacy, Secretariat } from '../../store/models';
import BaseGridXL from '../common/grid/BaseGridXL';
import DeleteCell from '../common/grid/DeleteCell';
import withTranslate, { WithTranslateProps } from '../common/WithTranslate';
import styled from '@emotion/styled';
import { IItemRendererProps, MultiSelect } from '@blueprintjs/select';

const Container = styled.div`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
`;

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: start;
  gap: 20px;
  align-content: center;
`;

interface SecretariatPharmaciesTabProps extends WithTranslateProps {
  secretariat?: Instance<typeof Secretariat>;
  PharmacyStore?: Instance<typeof PharmacyStore>;
  PharmacyDetailedStore?: Instance<typeof PharmacyDetailedStore>;
}

interface SecretariatPharmaciesTabState {
  isLoading: boolean;
  pharmaciesToLink: Array<Instance<typeof Pharmacy>>;
}

@inject('PharmacyStore')
@inject('PharmacyDetailedStore')
@observer
class SecretariatPharmaciesTab extends React.Component<SecretariatPharmaciesTabProps, SecretariatPharmaciesTabState> {
  state = {
    isLoading: false,
    pharmaciesToLink: [],
  } as SecretariatPharmaciesTabState;

  _isMounted = false;

  async componentDidMount() {
    this._isMounted = true;
    this._isMounted && this.setState({ isLoading: true });
    await this.props.PharmacyStore!.checkListLoaded(); // loadList();
    this._isMounted && this.setState({ isLoading: false });
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  private pharmaciesWithoutSecretariat = () => {
    return this.props
      .PharmacyStore!.dataList.filter((elem) => {
        return elem.secretariatId === undefined;
      })
      .filter((elem) => this.state.pharmaciesToLink.findIndex((p) => p.pharmacyId === elem.pharmacyId) === -1);
  };

  private pharmaciesItemRenderer = (
    item: Instance<typeof Pharmacy>,
    { handleClick, modifiers }: IItemRendererProps,
  ) => {
    if (!modifiers.matchesPredicate) return null;
    return (
      <MenuItem
        active={modifiers.active}
        key={item.pharmacyId}
        onClick={handleClick}
        text={item.apbNumber.toString()}
      />
    );
  };

  private onPharmacySelected = (item: Instance<typeof Pharmacy>) => {
    this.setState({ pharmaciesToLink: [...this.state.pharmaciesToLink, item] });
  };

  private onPharmacyRemoved = (value: string, index: number) => {
    this.setState({
      pharmaciesToLink: this.state.pharmaciesToLink.filter((elem) => elem.apbNumber.toString() !== value),
    });
  };

  private linkSelectedPharmaciesToSecretariat = () => {
    if (this.props.secretariat !== undefined)
      this.state.pharmaciesToLink.forEach(async (element) => {
        await this.props.PharmacyDetailedStore!.getByIdWithDetails(element.pharmacyId);
        const pharmacyToSave = this.props.PharmacyDetailedStore!.getById(element.pharmacyId);
        this.props.PharmacyDetailedStore!.saveElement({
          ...pharmacyToSave,
          secretariatId: this.props.secretariat!.secretariatId,
        });
      });
  };

  private getColumnDefs = (): ColDef[] => {
    const { translate } = this.props;
    return [
      {
        headerName: translate(TLCode.PHARMACIES_ApbNum),
        field: 'apbNumber',
        resizable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_PharmacyName),
        field: 'name',
        resizable: true,
      },
      {
        headerName: translate(TLCode.PHARMACIES_Group),
        field: 'group',
        resizable: true,
      },
      {
        headerName: '',
        resizable: true,
        cellRendererFramework: DeleteCell,
        cellRendererParams: (params: ICellRendererParams) => {
          return {
            onDeleteButtonClick: () => {
              this.unlinkPharmacy(params.data.pharmacyId);
            },
          };
        },
      },
    ];
  };

  private buildGridData = () => {
    return this.props
      .PharmacyStore!.dataList.filter((elem) => {
        return this.props.secretariat !== undefined && elem.secretariatId === this.props.secretariat!.secretariatId;
      })
      .map((p) => ({
        pharmacyId: p.pharmacyId,
        apbNumber: p.apbNumber,
        name: p.name,
        group: p.group,
        secretariatId: p.secretariatId,
      }));
  };

  private unlinkPharmacy = async (pharmacyId: number) => {
    console.log('pharmacy is ' + pharmacyId);
    await this.props.PharmacyDetailedStore!.getByIdWithDetails(pharmacyId);
    const pharmacyToSave = this.props.PharmacyDetailedStore!.getById(pharmacyId);
    this.props.PharmacyDetailedStore!.saveElement({ ...pharmacyToSave, secretariatId: undefined });
  };

  render() {
    return (
      <Container>
        <DivLine>
          <MultiSelect
            itemRenderer={this.pharmaciesItemRenderer}
            items={this.pharmaciesWithoutSecretariat()}
            onItemSelect={this.onPharmacySelected}
            tagRenderer={(item: Instance<typeof Pharmacy>) => item.apbNumber.toString()}
            itemPredicate={(query, item) => {
              return item.apbNumber
                .toString()
                .toLocaleLowerCase()
                .includes(query.toLocaleLowerCase());
            }}
            tagInputProps={{
              onRemove: this.onPharmacyRemoved,
            }}
            selectedItems={this.state.pharmaciesToLink}
            resetOnSelect
            placeholder="Select Pharmacies to link"
          />
          <Button
            onClick={this.linkSelectedPharmaciesToSecretariat}
            text={this.props.translate(TLCode.SECRETARIAT_LinkPharmacy)}
            disabled={this.state.pharmaciesToLink.length === 0}
          />
        </DivLine>
        <br />
        <br />
        <BaseGridXL
          keyName="pharmacyId"
          columnDefs={this.getColumnDefs()}
          rowData={this.buildGridData()}
          loading={this.state.isLoading}
          suppressClickEdit={true}
          getRowStyle={(params) => {
            if (params.data === undefined) return { 'background-color': Colors.LIGHT_GRAY1 };
            return { 'background-color': Colors.WHITE };
          }}
        />
      </Container>
    );
  }
}

export default withTranslate(SecretariatPharmaciesTab);
